// `useT()` is a client hook, and this header is rendered from BOTH sides: the
// client panels (`apps-tab`, `library-tabs`, `channel-panel-view`) inherited
// client-ness from their parents and worked, but `ChannelPageView` is an async
// SERVER component, so every `/c/<app>` render threw
// "Attempted to call useT() from the server" and returned 500 — all 156
// marketplace listing URLs in the sitemap, to users and crawlers alike. The
// directive is what makes the dual use legal; client components still
// server-render into the HTML, so the store page stays indexable.
'use client';

import Link from 'next/link';
import { useT } from '@/components/i18n-provider';
import { BadgeCheck, ShieldCheck } from 'lucide-react';
import { AppDescription } from './app-description';

/**
 * The app/agent store-page header (cover + icon + name + creator + description).
 * Shared by the published channel page AND the unpublished draft preview so the
 * two render identically — a draft is just this header with editable values and
 * a "Publish" action instead of "Add to workspace". The CTA row is passed as
 * children.
 */
export function AppStoreHeader({
  coverCss,
  bannerUrl,
  logoUrl,
  iconEmoji,
  fallbackIcon,
  icon,
  name,
  verified,
  pathLabel,
  creator,
  reputation = 0,
  description,
  children,
}: {
  coverCss: string;
  bannerUrl?: string | null;
  logoUrl?: string | null;
  iconEmoji?: string | null;
  fallbackIcon?: React.ReactNode;
  /** Fully-rendered icon node, replacing the default square chip. Used for agents
   *  (a round avatar) — by convention apps are square, agents are round. */
  icon?: React.ReactNode;
  name: string;
  verified?: boolean;
  pathLabel: string;
  creator?: { username: string | null; avatarUrl: string | null } | null;
  reputation?: number;
  description?: string | null;
  children?: React.ReactNode;
}) {
  const t = useT();
  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-background">
      <div className="h-28 w-full" style={{ background: bannerUrl ? undefined : coverCss }}>
        {bannerUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={bannerUrl} alt="" className="h-full w-full object-cover" />
        )}
      </div>

      <div className="px-5 pb-5">
        {icon ? (
          <div className="relative z-10 -mt-10 mb-3 h-16 w-16">{icon}</div>
        ) : (
          <div
            className="relative z-10 -mt-10 mb-3 flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border-2 border-background text-white"
            style={{ background: coverCss }}
          >
            {logoUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={logoUrl} alt="" className="h-full w-full object-cover" />
            ) : iconEmoji ? (
              <span className="text-3xl leading-none">{iconEmoji}</span>
            ) : (
              fallbackIcon ?? null
            )}
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <h1 className="truncate text-xl font-semibold">{name}</h1>
          {verified && <BadgeCheck className="h-5 w-5 shrink-0 text-sky-500" aria-label={t('workspace.panels.official')} />}
        </div>
        <div className="truncate text-sm text-muted-foreground">{pathLabel}</div>

        {creator?.username && (
          <div className="mt-2.5 flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">{t('marketplace.hub.by')}</span>
            {/* Encoded, not concatenated: a username is one path segment, and
                `deleteAgent` tombstones an account by renaming it to
                `<name>#del-<8hex>`. Interpolated raw, everything after the `#`
                became a fragment and the link went to a DIFFERENT, live
                profile at `/u/<name>`. Callers are expected to drop a
                tombstoned creator before it gets here (see the owner query in
                channel-page-view.tsx); this is the backstop for `?` and `%`. */}
            <Link
              href={`/u/${encodeURIComponent(creator.username)}`}
              className="inline-flex items-center gap-1.5 font-medium hover:underline"
            >
              {creator.avatarUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={creator.avatarUrl} alt="" className="h-5 w-5 rounded-full object-cover" />
              ) : (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[9px] font-medium uppercase">
                  {creator.username.slice(0, 2)}
                </span>
              )}
              @{creator.username}
            </Link>
            {reputation > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400"
                title={t('reputation.creatorTrustflowTitle')}
              >
                <ShieldCheck className="h-3 w-3" />
                {reputation}
              </span>
            )}
          </div>
        )}

        {description && <AppDescription text={description} />}

        {children && <div className="mt-5 flex flex-wrap items-center gap-2">{children}</div>}
      </div>
    </div>
  );
}
